/**
 * The composer's wiring: one draft, one write, two invalidations.
 *
 * ── Two invalidations, and both are load-bearing ──────────────────────────────────────────────
 * The session display, because the comment is part of what the panel draws — and the **attention
 * queue**, because writing the note is what makes `SessionAwaitingReview` stop being emitted.
 * Without the second one the trainer writes a comment, the task stays on the screen beside it, and
 * the only way to see the truth is a reload. Mobile invalidates the same two for the same reason.
 *
 * The queue's key is `entities/client`'s exported one rather than a local literal: the roster's
 * count, the detail's tasks card and this write all have to name the same cache.
 *
 * ── No optimistic patch ───────────────────────────────────────────────────────────────────────
 * Mobile patches its display cache optimistically, and it needs to: on a phone the round trip is
 * over a mobile link and the sheet closes immediately. Here the composer stays open, the control
 * says `Zapisuję…`, and the refetch lands in tens of milliseconds on a desktop connection. An
 * optimistic write would buy nothing and would need a rollback path plus a hand-seeded author
 * byline — two things that can be wrong — to buy it.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { attentionQueueQueryKey } from '~/entities/client';
import {
  getGetWorkoutExecutionIdDisplayQueryKey,
  usePatchWorkoutExecutionId,
} from '~/shared/api/generated/endpoints/workout-execution/workout-execution';

import { decideSessionComment, type SessionCommentDecision } from '../model/sessionComment';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';

export interface SessionCommentController {
  readonly draft: string;
  readonly setDraft: (value: string) => void;
  readonly decision: SessionCommentDecision;
  readonly saving: boolean;
  readonly failed: boolean;
  /**
   * The composer's resting state after a successful write — the confirmation is shown AND spoken.
   *
   * It holds until the trainer types again or resets, rather than lasting a single render: the
   * composer stays open with both controls refusing, so "saved" is what this surface IS until
   * something changes, and a timed toast would be a second mechanism saying the same thing worse.
   */
  readonly justSaved: boolean;
  readonly save: () => void;
  readonly reset: () => void;
}

export function useSessionComment(
  workoutExecutionId: number,
  savedComment: string,
): SessionCommentController {
  const contentLang = useContentLanguage();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(savedComment);
  const [justSaved, setJustSaved] = useState(false);

  const patch = usePatchWorkoutExecutionId();
  const decision = decideSessionComment({ draft, saved: savedComment });

  return {
    draft,
    setDraft: (value: string) => {
      setDraft(value);
      setJustSaved(false);
    },
    decision,
    saving: patch.isPending,
    failed: patch.isError,
    justSaved,
    save: () => {
      if (!decision.canSave || patch.isPending) return;
      patch.mutate(
        {
          id: workoutExecutionId,
          // The exercise content in the display response is localised, so the write carries the
          // CONTENT language for the response it gets back — never the reader's interface locale.
          params: { lang: contentLang },
          data: { trainerNote: decision.wireBody },
        },
        {
          onSuccess: () => {
            setJustSaved(true);
            // The generated key's first element is the URL and its second is the params object,
            // which carries `lang`. Invalidating the PREFIX drops every language variant — the
            // panel may hold a cached Polish and a cached English display of the same session, and
            // a comment written under one has to reach both.
            void queryClient.invalidateQueries({
              queryKey: getGetWorkoutExecutionIdDisplayQueryKey(workoutExecutionId),
            });
            void queryClient.invalidateQueries({ queryKey: attentionQueueQueryKey });
          },
        },
      );
    },
    reset: () => {
      setDraft(savedComment);
      setJustSaved(false);
      patch.reset();
    },
  };
}
